import { v4 as uuid } from 'uuid'
import User from './User'

function membersFromPG(data: any): User[] {
  if (Array.isArray(data.team_members)) {
    return data.team_members
      .map((row: any) => row.users)
      .filter(Boolean)
      .map((user: any) => User.fromPG(user))
  }
  if (Array.isArray(data.users)) {
    return data.users.map(User.fromPG)
  }
  return []
}

export interface TeamArgument {
  id: string
  name: string
  leaderID: string
  isPublic: boolean
  code: string
  members: User[]
  challengeID?: string
  targetMemberCount?: number
  recruitingMessage?: string
  gender?: string
  mergeOptIn?: boolean
  hasPaid?: boolean
}

export interface TeamCreateOptions {
  challengeID: string
  targetMemberCount?: number
  hasPaid?: boolean
}

export default class Team {
  readonly id: string
  readonly name: string
  readonly leaderID: string
  readonly isPublic: boolean
  readonly code: string
  readonly members: User[]
  readonly challengeID: string
  readonly targetMemberCount?: number
  readonly recruitingMessage?: string
  readonly gender?: string
  readonly mergeOptIn?: boolean
  readonly hasPaid?: boolean

  constructor(arg: TeamArgument) {
    this.id = arg.id
    this.name = arg.name
    this.leaderID = arg.leaderID
    this.isPublic = arg.isPublic
    this.code = arg.code
    this.members = arg.members
    if (!arg.challengeID) throw new Error('Team requires challengeID')
    this.challengeID = arg.challengeID
    this.targetMemberCount = arg.targetMemberCount
    this.recruitingMessage = arg.recruitingMessage
    this.gender = arg.gender
    this.mergeOptIn = arg.mergeOptIn
    this.hasPaid = arg.hasPaid
  }

  static create(name: string, leader: User, options: TeamCreateOptions) {
    return new Team({
      id: uuid(),
      name,
      leaderID: leader.id,
      code: this.generateCode(),
      isPublic: false,
      members: [leader],
      challengeID: options.challengeID,
      targetMemberCount: options.targetMemberCount,
      hasPaid: options.hasPaid,
    })
  }

  static fromPG(data: any): Team {
    return new Team({
      id: data.id,
      name: data.name,
      leaderID: data.leader_id,
      isPublic: data.is_public,
      code: data.code,
      members: membersFromPG(data),
      challengeID: data.challenge_id,
      targetMemberCount: data.target_member_count,
      recruitingMessage: data.recruiting_message,
      gender: data.gender,
      mergeOptIn: data.merge_opt_in,
      hasPaid: data.has_paid,
    })
  }

  toPG() {
    return {
      id: this.id,
      name: this.name,
      leader_id: this.leaderID,
      is_public: this.isPublic,
      code: this.code,
      challenge_id: this.challengeID,
      target_member_count: this.targetMemberCount,
      recruiting_message: this.recruitingMessage,
      gender: this.gender,
      merge_opt_in: this.mergeOptIn,
      has_paid: this.hasPaid,
    }
  }

  static generateCode() {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'

    const segment = () =>
      Array.from({ length: 3 }, () =>
        characters.charAt(Math.floor(Math.random() * characters.length)),
      ).join('')

    const code = `${segment()}-${segment()}`
    return code.replace(/[O0I1l]/g, 'W')
  }

  getLeader() {
    return this.members.find((member) => member.id === this.leaderID)
  }

  getNextLeader() {
    return this.members.filter((member) => member.id !== this.leaderID)[0]
  }

  isFull(maxSize: number) {
    return this.members.length >= maxSize
  }

  addMember(user: User, maxSize: number) {
    if (this.isFull(maxSize)) throw new Error('Team is full')
    return new Team({
      ...this,
      members: [...this.members, user],
    })
  }

  removeMember(user: User) {
    if (user.id === this.leaderID && this.members.length === 1) {
      throw new Error('Cannot remove the last member of a team')
    }

    const members = this.members.filter((member) => member.id !== user.id)
    const leaderID = this.leaderID === user.id ? members[0].id : this.leaderID

    return new Team({
      ...this,
      leaderID,
      members,
    })
  }

  hasMember(userID: string) {
    return this.members.some((member) => member.id === userID)
  }
}
